import type { VerificationLevel } from 'types/verification';
import ShieldCheck from 'svg/ShieldCheck';
import ShieldCheckTrusted from 'svg/ShieldCheckTrusted';
import classNames from 'styles/utils/classNames';

interface Props {
  level?: VerificationLevel;
  /** Sizes the badge; defaults to the 16px used on a 40px avatar. */
  className?: string;
  /** Renders in flow (e.g. beside a name) rather than pinned to an avatar corner. */
  inline?: boolean;
}

/**
 * The shield pinned to the corner of a player's avatar. Expects a `relative` parent.
 * Renders nothing when the player is neither verified nor trusted, so callers drop it
 * in without branching.
 */
export default function VerificationBadge({ level, className = 'size-4', inline = false }: Props) {
  if (!level) return null;

  const Icon = level === 'TRUSTED' ? ShieldCheckTrusted : ShieldCheck;

  // The ground is the page background, not white, so the badge doesn't punch a light
  // disc into a dark avatar. The text colour is what `ShieldCheck` fills from;
  // `ShieldCheckTrusted` bakes its own gradient and ignores it.
  return (
    <span
      className={classNames(
        'flex items-center justify-center rounded-full bg-color-bg-lightmode-primary p-[0.1rem] dark:bg-color-bg-darkmode-primary',
        !inline && 'absolute -bottom-0.5 -right-0.5',
        className,
      )}
    >
      <Icon className="size-full text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
    </span>
  );
}
